import type { OfflinePageRecord } from '../types';
import { findOfflinePageByUrl, putOfflinePage } from './db';
import { uid } from './ids';
import { proxiedUrl, snapshotUrl } from './urls';

interface SnapshotResponse {
  url: string;
  title: string;
  html: string;
  assets: string[];
  fetchedAt: number;
}

const MAX_INLINE_ASSET_BYTES = 600_000;
const MAX_ASSETS = 40;
const MAX_STYLESHEETS = 8;
const MAX_TOTAL_INLINE_BYTES = 8_000_000;

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error('Failed to read blob.'));
    reader.readAsDataURL(blob);
  });
}

function replaceAllLiteral(input: string, search: string, replacement: string): string {
  return input.split(search).join(replacement);
}

function injectOfflineBanner(html: string, originalUrl: string): string {
  const banner = `<div data-websurf-offline-banner style="position:fixed;z-index:2147483647;top:12px;left:50%;transform:translateX(-50%);font:600 13px system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#0f172a;color:#f8fafc;border:1px solid rgba(255,255,255,.18);border-radius:999px;padding:8px 12px;box-shadow:0 10px 30px rgba(0,0,0,.25)">Offline نسخة · ${originalUrl.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</div>`;
  if (/<body[^>]*>/i.test(html)) return html.replace(/<body([^>]*)>/i, `<body$1>${banner}`);
  return `${banner}${html}`;
}

async function fetchAssetDataUrl(assetUrl: string): Promise<string | undefined> {
  try {
    const response = await fetch(proxiedUrl(assetUrl));
    if (!response.ok) return undefined;
    const blob = await response.blob();
    if (blob.size > MAX_INLINE_ASSET_BYTES) return undefined;
    return await blobToDataUrl(blob);
  } catch {
    return undefined;
  }
}

async function fetchStylesheet(assetUrl: string, assetMap: Map<string, string>): Promise<string | undefined> {
  try {
    const response = await fetch(proxiedUrl(assetUrl));
    if (!response.ok) return undefined;
    let css = await response.text();
    assetMap.forEach((dataUrl, original) => { css = replaceAllLiteral(css, original, dataUrl); });
    return css.replace(/url\(([^)]+)\)/gi, (match, raw) => {
      const unquoted = String(raw).trim().replace(/^['"]|['"]$/g, '');
      try {
        const absolute = new URL(unquoted, assetUrl).href;
        const data = assetMap.get(absolute);
        return data ? `url("${data}")` : match;
      } catch {
        return match;
      }
    });
  } catch {
    return undefined;
  }
}

export async function savePageForOffline(url: string): Promise<OfflinePageRecord> {
  const response = await fetch(snapshotUrl(url));
  if (!response.ok) throw new Error(`Snapshot failed: ${response.status}`);
  const snapshot = await response.json() as SnapshotResponse;
  let html = snapshot.html;
  const assets = snapshot.assets.slice(0, MAX_ASSETS);
  const assetMap = new Map<string, string>();

  navigator.serviceWorker?.controller?.postMessage({
    type: 'WEBSURF_CACHE_URLS',
    urls: [proxiedUrl(snapshot.url), ...assets.map(proxiedUrl)]
  });

  let inlineBudget = MAX_TOTAL_INLINE_BYTES;
  const candidates = assets.filter((asset) => /\.(png|jpe?g|gif|webp|svg|ico|css|woff2?|ttf)(\?|#|$)/i.test(asset));
  for (const asset of candidates) {
    if (/\.css(\?|#|$)/i.test(asset)) continue;
    if (inlineBudget <= 0) break;
    const data = await fetchAssetDataUrl(asset);
    if (data) {
      // base64-decoded size approximation
      const approxBytes = Math.floor(data.length * 0.75);
      if (approxBytes > inlineBudget) continue;
      inlineBudget -= approxBytes;
      assetMap.set(asset, data);
    }
  }

  for (const asset of assets.filter((item) => /\.css(\?|#|$)/i.test(item)).slice(0, MAX_STYLESHEETS)) {
    const css = await fetchStylesheet(asset, assetMap);
    if (css) {
      const styleTag = `<style data-websurf-offline-stylesheet="${asset.replace(/"/g, '&quot;')}">${css.replace(/<\/style/gi, '<\\/style')}</style>`;
      html = html.replace(new RegExp(`<link[^>]+href=["']${asset.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["'][^>]*>`, 'i'), styleTag);
      html = replaceAllLiteral(html, asset, `data:text/css;charset=utf-8,${encodeURIComponent(css)}`);
    }
  }

  assetMap.forEach((dataUrl, original) => {
    html = replaceAllLiteral(html, original, dataUrl);
    try {
      const parsed = new URL(original);
      html = replaceAllLiteral(html, parsed.pathname + parsed.search, dataUrl);
    } catch {}
  });

  html = injectOfflineBanner(html, snapshot.url);

  const now = Date.now();
  const existing = await findOfflinePageByUrl(snapshot.url);
  const record: OfflinePageRecord = {
    id: existing?.id || uid('offline'),
    url: snapshot.url,
    title: snapshot.title || snapshot.url,
    html,
    assets,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    byteSize: new TextEncoder().encode(html).byteLength
  };
  try {
    await putOfflinePage(record);
  } catch (error) {
    const name = error instanceof DOMException ? error.name : '';
    const message = error instanceof Error ? error.message : '';
    if (name === 'QuotaExceededError' || /quota|internal error|InvalidState/i.test(message + name)) {
      throw new Error(`Browser storage is full (${(record.byteSize / 1_000_000).toFixed(1)} MB needed). Free up disk space, delete old offline pages from websurf://history, or try a smaller page.`);
    }
    throw error instanceof Error ? error : new Error('Offline save failed.');
  }
  return record;
}
